import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { X, ChevronLeft, ChevronRight, Check, Calendar as CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { Calendar } from './ui/calendar';
import { SERVICES, PROPERTY_TYPES, PROPERTY_SIZES } from '../data/content';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BookingModal = ({ open, onClose }) => {
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    property_type: '',
    service_type: '',
    property_size: '',
    preferred_date: '',
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  const total = 5;
  const progress = (step / total) * 100;

  const reset = () => {
    setStep(1);
    setSubmitted(false);
    setSubmitting(false);
    setError(null);
    setForm({
      property_type: '',
      service_type: '',
      property_size: '',
      preferred_date: '',
      name: '',
      email: '',
      phone: '',
      message: '',
    });
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await axios.post(`${API}/quote-request`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        service_type: form.service_type,
        property_type: form.property_type,
        message: `Size: ${form.property_size} · Preferred Date: ${form.preferred_date} · Notes: ${form.message || 'None'}`,
      });
      setSubmitted(true);
      setTimeout(handleClose, 3500);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error(err);
      }
      setError('Something went wrong. Please call (571) 265-6239.');
    } finally {
      setSubmitting(false);
    }
  };

  const next = () => step < total && setStep(step + 1);
  const prev = () => step > 1 && setStep(step - 1);

  const canAdvance = () => {
    if (step === 1) return !!form.property_type;
    if (step === 2) return !!form.service_type;
    if (step === 3) return !!form.property_size;
    if (step === 4) return !!form.preferred_date;
    return true;
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-0 md:p-6 bg-[#0D1B2A]/85 backdrop-blur-sm"
          data-testid="booking-modal"
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-3xl h-full md:h-auto md:max-h-[92vh] bg-[#F9F7F4] overflow-y-auto"
          >
            {/* Header */}
            <div className="sticky top-0 bg-[#F9F7F4] z-10 px-8 md:px-12 pt-8 pb-6 border-b border-[#0D1B2A]/10">
              <div className="flex justify-between items-start">
                <div>
                  <div className="text-[#C9A84C] text-[10px] tracking-[0.3em] uppercase mb-2">
                    Step {step} of {total}
                  </div>
                  <div className="font-serif text-2xl md:text-3xl text-[#0D1B2A]">
                    {submitted
                      ? 'Quote Received'
                      : ['Property type', 'Service needed', 'Property size', 'Preferred date', 'Your details'][step - 1]}
                  </div>
                </div>
                <button
                  onClick={handleClose}
                  className="p-2 -m-2 text-[#0D1B2A]/50 hover:text-[#0D1B2A] transition-colors"
                  data-testid="modal-close-btn"
                  aria-label="Close"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="mt-6 h-px bg-[#0D1B2A]/10 relative overflow-hidden">
                <motion.div
                  className="absolute inset-y-0 left-0 bg-[#C9A84C]"
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                />
              </div>
            </div>

            {/* Body */}
            <div className="p-8 md:p-12">
              {submitted ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-16"
                  data-testid="booking-success"
                >
                  <div className="w-20 h-20 rounded-full bg-[#C9A84C] flex items-center justify-center mx-auto mb-8">
                    <Check className="w-10 h-10 text-[#0D1B2A]" strokeWidth={2.5} />
                  </div>
                  <h3 className="font-serif text-3xl md:text-4xl text-[#0D1B2A] mb-4">
                    Thank you, {form.name.split(' ')[0]}.
                  </h3>
                  <p className="text-[#0D1B2A]/60 max-w-md mx-auto leading-relaxed">
                    We've received your request. A senior team member will reach
                    out within two hours with a tailored quote.
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={step}
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -30 }}
                      transition={{ duration: 0.3 }}
                    >
                      {step === 1 && (
                        <div className="grid grid-cols-2 gap-3 md:gap-4">
                          {PROPERTY_TYPES.map((p) => {
                            const Icon = p.icon;
                            const selected = form.property_type === p.label;
                            return (
                              <button
                                key={p.id}
                                type="button"
                                onClick={() => {
                                  setForm({ ...form, property_type: p.label });
                                  setTimeout(next, 200);
                                }}
                                data-testid={`booking-property-${p.id}`}
                                className={`group p-6 md:p-8 border text-left transition-all ${
                                  selected
                                    ? 'border-[#C9A84C] bg-[#C9A84C]/10'
                                    : 'border-[#0D1B2A]/15 hover:border-[#0D1B2A]/40'
                                }`}
                              >
                                <Icon className={`w-7 h-7 mb-4 ${selected ? 'text-[#C9A84C]' : 'text-[#0D1B2A]/60 group-hover:text-[#0D1B2A]'}`} />
                                <div className="font-serif text-lg md:text-xl text-[#0D1B2A]">{p.label}</div>
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {step === 2 && (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {SERVICES.map((s) => {
                            const Icon = s.icon;
                            const selected = form.service_type === s.name;
                            return (
                              <button
                                key={s.id}
                                type="button"
                                onClick={() => {
                                  setForm({ ...form, service_type: s.name });
                                  setTimeout(next, 200);
                                }}
                                data-testid={`booking-service-${s.id}`}
                                className={`group p-5 md:p-6 border text-left transition-all ${
                                  selected
                                    ? 'border-[#C9A84C] bg-[#C9A84C]/10'
                                    : 'border-[#0D1B2A]/15 hover:border-[#0D1B2A]/40'
                                }`}
                              >
                                <Icon className={`w-6 h-6 mb-3 ${selected ? 'text-[#C9A84C]' : 'text-[#0D1B2A]/60 group-hover:text-[#0D1B2A]'}`} />
                                <div className="font-serif text-base text-[#0D1B2A]">{s.name}</div>
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {step === 3 && (
                        <div className="grid grid-cols-2 gap-3 md:gap-4">
                          {PROPERTY_SIZES.map((s) => {
                            const selected = form.property_size === s.label;
                            return (
                              <button
                                key={s.id}
                                type="button"
                                onClick={() => {
                                  setForm({ ...form, property_size: s.label });
                                  setTimeout(next, 200);
                                }}
                                data-testid={`booking-size-${s.id}`}
                                className={`p-6 md:p-8 border text-left transition-all ${
                                  selected
                                    ? 'border-[#C9A84C] bg-[#C9A84C]/10'
                                    : 'border-[#0D1B2A]/15 hover:border-[#0D1B2A]/40'
                                }`}
                              >
                                <div className="font-serif text-xl md:text-2xl text-[#0D1B2A] mb-1">{s.label}</div>
                                <div className="text-sm text-[#0D1B2A]/50">{s.sub}</div>
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {step === 4 && (
                        <div className="space-y-6">
                          <div className="text-xs tracking-[0.25em] uppercase text-[#0D1B2A]/60 flex items-center gap-2">
                            <CalendarIcon className="w-3.5 h-3.5" /> Choose a date
                          </div>
                          <div className="font-serif text-2xl md:text-3xl text-[#0D1B2A] min-h-[2.5rem]" data-testid="booking-selected-date">
                            {form.preferred_date
                              ? format(new Date(form.preferred_date + 'T00:00:00'), 'EEEE, MMMM d, yyyy')
                              : <span className="text-[#0D1B2A]/30 italic">Select your preferred date</span>}
                          </div>
                          <div className="bg-white border border-[#0D1B2A]/10 p-2 md:p-4 inline-block w-full md:w-auto luxury-calendar" data-testid="booking-date-input">
                            <Calendar
                              mode="single"
                              selected={form.preferred_date ? new Date(form.preferred_date + 'T00:00:00') : undefined}
                              onSelect={(d) => {
                                if (!d) return;
                                const yyyy = d.getFullYear();
                                const mm = String(d.getMonth() + 1).padStart(2, '0');
                                const dd = String(d.getDate()).padStart(2, '0');
                                setForm({ ...form, preferred_date: `${yyyy}-${mm}-${dd}` });
                              }}
                              disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                              initialFocus
                            />
                          </div>
                          <p className="text-sm text-[#0D1B2A]/50">
                            We'll confirm availability with you. Same-day service available for Airbnb turnovers.
                          </p>
                        </div>
                      )}

                      {step === 5 && (
                        <div className="space-y-6">
                          {[
                            { key: 'name', label: 'Full name', placeholder: 'Jane Smith', type: 'text', required: true },
                            { key: 'email', label: 'Email', placeholder: 'jane@example.com', type: 'email', required: true },
                            { key: 'phone', label: 'Phone', placeholder: '(555) 123-4567', type: 'tel', required: true },
                          ].map((f) => (
                            <label key={f.key} className="block">
                              <div className="text-xs tracking-[0.25em] uppercase text-[#0D1B2A]/60 mb-2">{f.label}</div>
                              <input
                                type={f.type}
                                required={f.required}
                                value={form[f.key]}
                                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                                placeholder={f.placeholder}
                                data-testid={`booking-${f.key}-input`}
                                className="w-full bg-transparent border-b border-[#0D1B2A]/20 focus:border-[#C9A84C] py-3 text-lg text-[#0D1B2A] outline-none transition-colors placeholder:text-[#0D1B2A]/30"
                              />
                            </label>
                          ))}
                          <label className="block">
                            <div className="text-xs tracking-[0.25em] uppercase text-[#0D1B2A]/60 mb-2">Notes (optional)</div>
                            <textarea
                              value={form.message}
                              onChange={(e) => setForm({ ...form, message: e.target.value })}
                              rows="3"
                              placeholder="Anything else we should know?"
                              data-testid="booking-message-input"
                              className="w-full bg-transparent border-b border-[#0D1B2A]/20 focus:border-[#C9A84C] py-3 text-base text-[#0D1B2A] outline-none transition-colors resize-none placeholder:text-[#0D1B2A]/30"
                            />
                          </label>
                          {error && (
                            <p className="text-sm text-red-600" data-testid="booking-error">{error}</p>
                          )}
                        </div>
                      )}
                    </motion.div>
                  </AnimatePresence>

                  {/* Footer nav */}
                  <div className="mt-12 flex items-center justify-between gap-4">
                    {step > 1 ? (
                      <button
                        type="button"
                        onClick={prev}
                        className="flex items-center gap-2 text-sm tracking-[0.2em] uppercase text-[#0D1B2A]/60 hover:text-[#0D1B2A] transition-colors"
                        data-testid="booking-back-btn"
                      >
                        <ChevronLeft className="w-4 h-4" /> Back
                      </button>
                    ) : (
                      <div />
                    )}

                    {step < total && step > 3 && (
                      <button
                        type="button"
                        onClick={next}
                        disabled={!canAdvance()}
                        className="flex items-center gap-2 bg-[#0D1B2A] text-white px-8 py-3 text-xs tracking-[0.25em] uppercase disabled:opacity-30"
                        data-testid="booking-next-btn"
                      >
                        Next <ChevronRight className="w-4 h-4" />
                      </button>
                    )}

                    {step === total && (
                      <button
                        type="submit"
                        disabled={submitting || !form.name || !form.email || !form.phone}
                        className="group relative overflow-hidden bg-[#C9A84C] text-[#0D1B2A] px-10 py-4 text-xs tracking-[0.25em] uppercase font-semibold disabled:opacity-50 flex items-center gap-3"
                        data-testid="booking-submit-btn"
                      >
                        {submitting ? 'Sending...' : 'Submit Request'}
                        {!submitting && <Check className="w-4 h-4" />}
                      </button>
                    )}
                  </div>
                </form>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
